/**
 * Clario supply-chain / Logistics screens.
 *
 * The StudyWorks Oracle EBS logistics feed lands in nine vendor-neutral
 * `logistics_*` core tables (materials, lots, locations, units, shipments,
 * shipment items, inventory, reconciliation, and chain-of-custody events).
 * Each is surfaced as its own dataset via the unified endpoint and rendered
 * with the shared `PageTemplate` (which wraps `MetadataDashboardTemplate` —
 * the table + dashboard advanced view used by the Measures screens), one
 * sub-nav per dataset (mirroring the imaging datasets' 1:1 dataset->screen map).
 */
import { PageTemplate } from "@/components/Templates/PageTemplate";

type LogisticsScreenProps = {
  title: string;
  tooltipText: string;
  /** dataset_type — must match a DATASET_CONFIGS key in the backend. */
  type: string;
  /** Stable per-row identity column for table selection (the dataset PK). */
  selectionIdentifierColumn: string;
  wikiLink: string;
};

function LogisticsScreen({
  title,
  tooltipText,
  type,
  selectionIdentifierColumn,
  wikiLink,
}: LogisticsScreenProps) {
  return (
    <PageTemplate
      title={title}
      tooltipText={tooltipText}
      type={type}
      constants={{ wikiLink, idLink: undefined }}
      selectionIdentifierColumn={selectionIdentifierColumn}
    />
  );
}

export function LogisticsMaterials() {
  return (
    <LogisticsScreen
      title="Logistics: Materials"
      tooltipText="Vendor-neutral catalog of trackable supply materials (IP, device, kit, specimen, supply), including blinding and serialization flags."
      type="logistics_material"
      selectionIdentifierColumn="logistics_material_id"
      wikiLink="/wiki/guides/exploration-logistics-materials"
    />
  );
}

export function LogisticsLots() {
  return (
    <LogisticsScreen
      title="Logistics: Lots"
      tooltipText="Lots/batches of a material with manufacture and expiry dates (IP finished lots, kit lots, consumables)."
      type="logistics_lot"
      selectionIdentifierColumn="logistics_lot_id"
      wikiLink="/wiki/guides/exploration-logistics-lots"
    />
  );
}

export function LogisticsLocations() {
  return (
    <LogisticsScreen
      title="Logistics: Locations"
      tooltipText="Supply-network nodes: depots, vendors, central labs, study sites, and destruction facilities."
      type="logistics_location"
      selectionIdentifierColumn="logistics_location_id"
      wikiLink="/wiki/guides/exploration-logistics-locations"
    />
  );
}

export function LogisticsUnits() {
  return (
    <LogisticsScreen
      title="Logistics: Units"
      tooltipText="Individually tracked supply units (drug units, serialized devices, kits, specimens) with current status, location, and assignment."
      type="logistics_unit"
      selectionIdentifierColumn="logistics_unit_id"
      wikiLink="/wiki/guides/exploration-logistics-units"
    />
  );
}

export function LogisticsShipments() {
  return (
    <LogisticsScreen
      title="Logistics: Shipments"
      tooltipText="Supply orders/shipments moving material between locations (depot to site, resupply, returns), with carrier, status, and date lifecycle."
      type="logistics_shipment"
      selectionIdentifierColumn="logistics_shipment_id"
      wikiLink="/wiki/guides/exploration-logistics-shipments"
    />
  );
}

export function LogisticsShipmentItems() {
  return (
    <LogisticsScreen
      title="Logistics: Shipment Items"
      tooltipText="Line items within a shipment (material/lot/unit plus ordered, shipped, and received quantities)."
      type="logistics_shipment_item"
      selectionIdentifierColumn="logistics_shipment_item_id"
      wikiLink="/wiki/guides/exploration-logistics-shipment-items"
    />
  );
}

export function LogisticsInventory() {
  return (
    <LogisticsScreen
      title="Logistics: Inventory"
      tooltipText="Stock-on-hand and site-allocation snapshots by location, material, and lot for supply monitoring and resupply."
      type="logistics_inventory"
      selectionIdentifierColumn="logistics_inventory_id"
      wikiLink="/wiki/guides/exploration-logistics-inventory"
    />
  );
}

export function LogisticsReconciliation() {
  return (
    <LogisticsScreen
      title="Logistics: Reconciliation"
      tooltipText="Supply accountability and reconciliation of units (generalized drug accountability): supplied, used, remaining, and missing quantities with site and monitor sign-off."
      type="logistics_reconciliation"
      selectionIdentifierColumn="logistics_reconciliation_id"
      wikiLink="/wiki/guides/exploration-logistics-reconciliation"
    />
  );
}

export function LogisticsEvents() {
  return (
    <LogisticsScreen
      title="Logistics: Chain of Custody"
      tooltipText="Chain-of-custody event log (EPCIS-style what/when/where/disposition) across all logistics types and vendors."
      type="logistics_event"
      selectionIdentifierColumn="logistics_event_id"
      wikiLink="/wiki/guides/exploration-logistics-events"
    />
  );
}
